import { FormHelperText, Grid, IconButton, TextField, Typography } from '@mui/material';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useCallback } from 'react';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import FormDialog from '../Dialogs/FormDialog';
import ReactHookFormCheckbox from '../ReactHookForm/ReactHookFormCheckbox';
import ReactHookFormTextField from '../ReactHookForm/ReactHookFormTextField';
import { ApiKeyInterface, ApiScopeInterface } from '../../types/ApiKey.interface';
import { copyToClipboard } from '../../utils';
import { useDispatchSuccessNotification } from '../../hooks';

const ApiKeyDialog = ({
    apiKey,
    showDialog,
    permissions,
    onSubmit,
    isLoading,
    onClose,
}: {
    apiKey?: ApiKeyInterface;
    showDialog: boolean;
    permissions?: ApiScopeInterface[];
    onSubmit: any;
    isLoading: boolean;
    onClose: any;
}) => {
    const { t } = useTranslation();
    const { dispatchSuccessNotification } = useDispatchSuccessNotification();

    const form = useForm({
        defaultValues: {
            id: apiKey?.id || '',
            name: apiKey?.name || '',
            client_id: apiKey?.client_id || '',
            client_secret: apiKey?.client_secret || '',
            scope: apiKey?.scope || [],
            generateToken: true,
        },
    });
    const {
        formState: { errors },
        control,
    } = form;

    const handleClose = () => {
        onClose();
    };

    const handleCopyValue = useCallback(
        async (value: string, name = 'Value') => {
            await copyToClipboard(value);
            dispatchSuccessNotification({ message: t('apiKey.copiedToClipboard', { name }) });
        },
        [dispatchSuccessNotification, t]
    );

    return (
        <FormDialog
            title={apiKey?.id ? t('apiKey.editTitle') : t('apiKey.createTitle')}
            showDialog={showDialog}
            form={form}
            onSubmit={onSubmit}
            onClose={handleClose}
            isLoading={isLoading}
            id="api-key-dialog"
        >
            <Grid item xs={12}>
                <ReactHookFormTextField
                    name="name"
                    label={t('field.name')}
                    control={control}
                    rules={{ required: { value: true, message: t('validation.required') } }}
                    formErrors={errors}
                    isNumeric={false}
                    defaultValue=""
                    inputProps={undefined}
                    isMultiline={false}
                    size={apiKey?.id ? 'small' : 'medium'}
                ></ReactHookFormTextField>
            </Grid>
            {apiKey?.id && (
                <>
                    <Grid item xs={12}>
                        <TextField
                            fullWidth
                            label={t('field.clientId')}
                            value={apiKey.client_id}
                            size={'small'}
                            disabled
                            InputProps={{
                                endAdornment: (
                                    <IconButton
                                        size="small"
                                        onClick={() => handleCopyValue(apiKey.client_id, t('field.clientId'))}
                                    >
                                        <ContentCopyIcon fontSize="small" />
                                    </IconButton>
                                ),
                            }}
                        />
                    </Grid>
                    <Grid item xs={12}>
                        <TextField
                            fullWidth
                            label={t('field.clientSecret')}
                            value={apiKey.client_secret}
                            size={'small'}
                            disabled
                            InputProps={{
                                endAdornment: (
                                    <IconButton
                                        size="small"
                                        onClick={() => handleCopyValue(apiKey.client_secret, t('field.clientSecret'))}
                                    >
                                        <ContentCopyIcon fontSize="small" />
                                    </IconButton>
                                ),
                            }}
                        />
                    </Grid>
                </>
            )}
            <Grid item xs={12}>
                <Typography variant={'h6'}>{t('apiKey.permissions')}</Typography>
            </Grid>
            {permissions &&
                permissions.map((permission: ApiScopeInterface) => (
                    <Grid
                        item
                        xs={6}
                        key={permission.value}
                        sx={{
                            paddingTop: `.75rem !important`,
                        }}
                    >
                        <ReactHookFormCheckbox
                            name="scope"
                            control={control}
                            size="small"
                            disableRipple={true}
                            label={
                                <>
                                    <Typography variant="body2">{permission.description}</Typography>
                                    <Typography variant="caption" color="gray">
                                        {permission.value}
                                    </Typography>
                                </>
                            }
                            labelPlacement="end"
                            value={permission.value}
                            multiple={true}
                            checked={apiKey?.scope?.includes(permission.value)}
                        ></ReactHookFormCheckbox>
                    </Grid>
                ))}
            <Grid item xs={12} pt={0}>
                <ReactHookFormCheckbox
                    name="generateToken"
                    control={control}
                    label={apiKey?.id ? t('apiKey.regenerateToken') : t('apiKey.createToken')}
                    labelPlacement="end"
                />
                {apiKey?.id && <FormHelperText>{t('apiKey.regenerateHint')}</FormHelperText>}
            </Grid>
        </FormDialog>
    );
};

export default ApiKeyDialog;
